import { useTranslation } from 'react-i18next';

interface ProfileSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  fullName: string;
  email: string;
  onSignOut: () => void;
  signingOut: boolean;
}

const tabs = [
  { id: 'bookings', label: 'profile.tabBookings', icon: 'ri-suitcase-line' },
  { id: 'saved', label: 'profile.tabSaved', icon: 'ri-heart-3-line' },
  { id: 'settings', label: 'profile.tabSettings', icon: 'ri-settings-3-line' },
];

export default function ProfileSidebar({
  activeTab,
  onTabChange,
  fullName,
  email,
  onSignOut,
  signingOut,
}: ProfileSidebarProps) {
  const { t } = useTranslation();
  const initial = fullName?.trim()?.charAt(0)?.toUpperCase() || 'V';

  return (
    <aside className="w-full lg:w-[300px] shrink-0">
      <div className="bg-white rounded-lg border border-secondary-200 p-6">
        {/* Avatar + name */}
        <div className="flex lg:flex-col items-center lg:text-center gap-4">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
            <span className="font-heading text-2xl md:text-3xl font-bold">{initial}</span>
          </div>
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-bold text-secondary-900 truncate">
              {fullName || t('profile.traveler')}
            </h2>
            <p className="mt-0.5 text-sm text-secondary-500 truncate">{email}</p>
          </div>
        </div>

        {/* Nav tabs */}
        <nav className="mt-6 space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`w-full flex items-center gap-3 text-sm font-medium rounded-md px-4 py-2.5 transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-white'
                  : 'text-secondary-700 hover:bg-secondary-100'
              }`}
            >
              <i className={`${tab.icon} text-base`} />
              {t(tab.label)}
            </button>
          ))}
        </nav>

        {/* Sign out */}
        <button
          onClick={onSignOut}
          disabled={signingOut}
          className="mt-6 w-full flex items-center justify-center gap-2 text-sm font-medium text-secondary-600 hover:text-red-600 border border-secondary-300 rounded-md px-4 py-2.5 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
        >
          {signingOut ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base" />
              {t('profile.signingOut')}
            </>
          ) : (
            <>
              <i className="ri-logout-box-r-line text-base" />
              {t('profile.signOut')}
            </>
          )}
        </button>
      </div>
    </aside>
  );
}